/**
 * handlers/mode.ts
 * Chat / Draft / Prep mode switching.
 */

import { state } from '../state';
import { dom } from '../dom';
import { isClientUser } from './helpers';

/**
 * Switch between 'chat', 'draft', and 'prep' modes.
 * Updates state, button active-classes, dropdown visibility, and placeholder text.
 */
export function setMode(mode: 'chat' | 'draft' | 'prep') {
  // Clients never see Draft or Prep mode.
  if ((mode === 'draft' || mode === 'prep') && isClientUser()) return;

  // Draft and Prep require an active matter context so the backend can scope
  // the request.  On the advocate portal this is set by openMatterDetail();
  // on the admin portal it is set by the matter sidebar click handler.
  // Show a clear toast rather than silently firing a 400.
  if ((mode === 'draft' || mode === 'prep') && !state.currentMatterId) {
    // Import showToast lazily to avoid a circular dep (ui-renderers → dom → state)
    import('../ui-renderers.js').then(m => {
      m.showToast(
        mode === 'draft'
          ? 'Open a matter first to use Draft mode.'
          : 'Open a matter first to use Prep mode.',
        'info',
      );
    }).catch(() => {});
    return;
  }

  state.draftMode = (mode === 'draft');
  state.prepMode  = (mode === 'prep');

  const chatBtn  = dom.modeChatBtn;
  const draftBtn = dom.modeDraftBtn;
  const prepBtn  = dom.modePrepBtn;
  const draftSel = dom.draftType;
  const prepSel  = dom.prepType;

  if (chatBtn)  chatBtn.classList.toggle('active', mode === 'chat');
  if (draftBtn) draftBtn.classList.toggle('active', mode === 'draft');
  if (prepBtn)  prepBtn.classList.toggle('active',  mode === 'prep');

  if (draftSel) draftSel.style.display = mode === 'draft' ? '' : 'none';
  if (prepSel)  prepSel.style.display  = mode === 'prep'  ? '' : 'none';

  const inputEl = dom.input;
  if (inputEl) {
    if (mode === 'draft') {
      inputEl.placeholder = 'Describe the document you need drafted\u2026';
    } else if (mode === 'prep') {
      inputEl.placeholder = 'Ask for legal reasoning, e.g. \u201cDraft an argument for adverse possession\u201d\u2026';
    } else {
      inputEl.placeholder = 'Ask me anything about the platform\u2026';
    }
  }

  if (prepSel) state.prepType = prepSel.value || 'argument';
}

/**
 * Backwards-compatible wrapper — used by existing callers and tests.
 */
export function setDraftMode(on: boolean) {
  setMode(on ? 'draft' : 'chat');
}
